
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */



const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "hirer-identifiers"
    }}>{`Hirer identifiers`}</h1>
    <p>{`The SEEK API uses opaque `}<a parentName="p" {...{
        "href": "/graphql/seek-api-conventions#object-identifiers"
      }}>{`object identifier`}</a>{` strings to identify SEEK hirers.
Your software can programmatically convert between our numeric SEEK advertiser IDs and the new hirer IDs.
However, you’re encouraged to directly use hirer IDs whenever possible.`}</p>
    <p>{`The “Look up a hirer by ID” section of the `}<a parentName="p" {...{
        "href": "/manage/hirers"
      }}>{`Developer Dashboard’s hirers page`}</a>{` can search for a hirer by either identifier.
You can then view all of their identifiers along with their `}<a parentName="p" {...{
        "href": "/auth/hirer-relationships"
      }}>{`active relationships`}</a>{`.`}</p>
    <h2 {...{
      "id": "converting-from-a-seek-advertiser-id"
    }}>{`Converting from a SEEK advertiser ID`}</h2>
    <p>{`The `}<a parentName="p" {...{
        "href": "/schema/query/seekAnzAdvertiser"
      }}><inlineCode parentName="a">{`seekAnzAdvertiser`}</inlineCode>{` query`}</a>{` looks up a `}<a parentName="p" {...{
        "href": "/schema/named-type/HiringOrganization"
      }}><inlineCode parentName="a">{`HiringOrganization`}</inlineCode></a>{` from a SEEK advertiser ID.
You can select the resulting `}<inlineCode parentName="p">{`HiringOrganization`}</inlineCode>{`’s hirer ID:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":\"lovato-ignore-no-deprecated-schema\",\"value\":\"query ($legacyId: Int!) {\\n  seekAnzAdvertiser(id: $legacyId) {\\n    id {\\n      value\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":15,\"column\":1,\"offset\":684},\"end\":{\"line\":23,\"column\":4,\"offset\":829},\"indent\":[1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"legacyId\\\": 10000\\n}\",\"position\":{\"start\":{\"line\":25,\"column\":1,\"offset\":831},\"end\":{\"line\":29,\"column\":4,\"offset\":866},\"indent\":[1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"seekAnzAdvertiser\\\": {\\n    \\\"id\\\": {\\n      \\\"value\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\"\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":31,\"column\":1,\"offset\":868},\"end\":{\"line\":39,\"column\":4,\"offset\":992},\"indent\":[1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":\"lovato-ignore-no-deprecated-schema\",\"value\":\"query": true,
        "($legacyId:": true,
        "Int!)": true,
        "{\\n": true,
        "": true,
        "seekAnzAdvertiser(id:": true,
        "$legacyId)": true,
        "id": true,
        "value\\n": true,
        "}\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":15,\"column\":1,\"offset\":684},\"end\":{\"line\":23,\"column\":4,\"offset\":829},\"indent\":[1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"legacyId\\\":": true,
        "10000\\n}\",\"position\":{\"start\":{\"line\":25,\"column\":1,\"offset\":831},\"end\":{\"line\":29,\"column\":4,\"offset\":866},\"indent\":[1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"seekAnzAdvertiser\\\":": true,
        "\\\"id\\\":": true,
        "\\\"value\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":31,\"column\":1,\"offset\":868},\"end\":{\"line\":39,\"column\":4,\"offset\":992},\"indent\":[1,1,1,1,1,1,1,1]}}]": true
      }}>{`query ($legacyId: Int!) {
  seekAnzAdvertiser(id: $legacyId) {
    id {
      value
    }
  }
}
`}</code></pre>
    <p>{`The `}<a parentName="p" {...{
        "href": "/schema/query/hiringOrganizations"
      }}><inlineCode parentName="a">{`hiringOrganizations`}</inlineCode>{` query`}</a>{` will return a paginated list of all the SEEK hirers you have a `}<a parentName="p" {...{
        "href": "/auth/hirer-relationships"
      }}>{`relationship`}</a>{` with.
You can select both the `}<inlineCode parentName="p">{`id`}</inlineCode>{` & `}<inlineCode parentName="p">{`seekAnzAdvertiserId`}</inlineCode>{` fields to produce a mapping between the identifiers:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":\"lovato-ignore-no-deprecated-schema\",\"value\":\"query ($schemeId: String!, $first: Int) {\\n  hiringOrganizations(schemeId: $schemeId, first: $first) {\\n    edges {\\n      node {\\n        id {\\n          value\\n        }\\n        seekAnzAdvertiserId\\n        name\\n      }\\n    }\\n    pageInfo {\\n      hasNextPage\\n      endCursor\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":44,\"column\":1,\"offset\":1219},\"end\":{\"line\":62,\"column\":4,\"offset\":1550},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"schemeId\\\": \\\"seekAnzPublicTest\\\",\\n  \\\"first\\\": 5\\n}\",\"position\":{\"start\":{\"line\":64,\"column\":1,\"offset\":1552},\"end\":{\"line\":69,\"column\":4,\"offset\":1615},\"indent\":[1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"hiringOrganizations\\\": {\\n    \\\"edges\\\": [\\n      {\\n        \\\"node\\\": {\\n          \\\"id\\\": {\\n            \\\"value\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\"\\n          },\\n          \\\"seekAnzAdvertiserId\\\": 10000,\\n          \\\"name\\\": \\\"Acme Corp\\\"\\n        }\\n      },\\n      {\\n        \\\"node\\\": {\\n          \\\"id\\\": {\\n            \\\"value\\\": \\\"seekAnzPublicTest:organization:seek:A3HL8E4w\\\"\\n          },\\n          \\\"seekAnzAdvertiserId\\\": 10002,\\n          \\\"name\\\": \\\"Contoso\\\"\\n        }\\n      },\\n      {\\n        \\\"node\\\": {\\n          \\\"id\\\": {\\n            \\\"value\\\": \\\"seekAnzPublicTest:organization:seek:AYAWCibZ\\\"\\n          },\\n          \\\"seekAnzAdvertiserId\\\": 10003,\\n          \\\"name\\\": \\\"Fabrikam\\\"\\n        }\\n      },\\n      {\\n        \\\"node\\\": {\\n          \\\"id\\\": {\\n            \\\"value\\\": \\\"seekAnzPublicTest:organization:seek:FWzEydsm\\\"\\n          },\\n          \\\"seekAnzAdvertiserId\\\": 10013,\\n          \\\"name\\\": \\\"PixelArcade Studios\\\"\\n        }\\n      },\\n      {\\n        \\\"node\\\": {\\n          \\\"id\\\": {\\n            \\\"value\\\": \\\"seekAnzPublicTest:organization:seek:C2p2SCBR\\\"\\n          },\\n          \\\"seekAnzAdvertiserId\\\": 10006,\\n          \\\"name\\\": \\\"Prescott Pharmaceuticals\\\"\\n        }\\n      }\\n    ],\\n    \\\"pageInfo\\\": {\\n      \\\"hasNextPage\\\": true,\\n      \\\"endCursor\\\": \\\"seekAnzPublicTest:hiringOrganizationCursor:seek:C2p2SCBR\\\"\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":71,\"column\":1,\"offset\":1617},\"end\":{\"line\":127,\"column\":4,\"offset\":2905},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":\"lovato-ignore-no-deprecated-schema\",\"value\":\"query": true,
        "($schemeId:": true,
        "String!,": true,
        "$first:": true,
        "Int)": true,
        "{\\n": true,
        "": true,
        "hiringOrganizations(schemeId:": true,
        "$schemeId,": true,
        "first:": true,
        "$first)": true,
        "edges": true,
        "node": true,
        "id": true,
        "value\\n": true,
        "}\\n": true,
        "seekAnzAdvertiserId\\n": true,
        "name\\n": true,
        "pageInfo": true,
        "hasNextPage\\n": true,
        "endCursor\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":44,\"column\":1,\"offset\":1219},\"end\":{\"line\":62,\"column\":4,\"offset\":1550},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"schemeId\\\":": true,
        "\\\"seekAnzPublicTest\\\",\\n": true,
        "\\\"first\\\":": true,
        "5\\n}\",\"position\":{\"start\":{\"line\":64,\"column\":1,\"offset\":1552},\"end\":{\"line\":69,\"column\":4,\"offset\":1615},\"indent\":[1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"hiringOrganizations\\\":": true,
        "\\\"edges\\\":": true,
        "[\\n": true,
        "\\\"node\\\":": true,
        "\\\"id\\\":": true,
        "\\\"value\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\"\\n": true,
        "},\\n": true,
        "\\\"seekAnzAdvertiserId\\\":": true,
        "10000,\\n": true,
        "\\\"name\\\":": true,
        "\\\"Acme": true,
        "Corp\\\"\\n": true,
        "\\\"seekAnzPublicTest:organization:seek:A3HL8E4w\\\"\\n": true,
        "10002,\\n": true,
        "\\\"Contoso\\\"\\n": true,
        "\\\"seekAnzPublicTest:organization:seek:AYAWCibZ\\\"\\n": true,
        "10003,\\n": true,
        "\\\"Fabrikam\\\"\\n": true,
        "\\\"seekAnzPublicTest:organization:seek:FWzEydsm\\\"\\n": true,
        "10013,\\n": true,
        "\\\"PixelArcade": true,
        "Studios\\\"\\n": true,
        "\\\"seekAnzPublicTest:organization:seek:C2p2SCBR\\\"\\n": true,
        "10006,\\n": true,
        "\\\"Prescott": true,
        "Pharmaceuticals\\\"\\n": true,
        "],\\n": true,
        "\\\"pageInfo\\\":": true,
        "\\\"hasNextPage\\\":": true,
        "true,\\n": true,
        "\\\"endCursor\\\":": true,
        "\\\"seekAnzPublicTest:hiringOrganizationCursor:seek:C2p2SCBR\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":71,\"column\":1,\"offset\":1617},\"end\":{\"line\":127,\"column\":4,\"offset\":2905},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`query ($schemeId: String!, $first: Int) {
  hiringOrganizations(schemeId: $schemeId, first: $first) {
    edges {
      node {
        id {
          value
        }
        seekAnzAdvertiserId
        name
      }
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
}
`}</code></pre>
    <h2 {...{
      "id": "selecting-an-objects-seek-advertiser-id"
    }}>{`Selecting an object’s SEEK advertiser ID`}</h2>
    <p><inlineCode parentName="p">{`HiringOrganization`}</inlineCode>{` objects have a `}<a parentName="p" {...{
        "href": "/schema/named-type/HiringOrganization#/field/seekAnzAdvertiserId"
      }}><inlineCode parentName="a">{`seekAnzAdvertiserId`}</inlineCode>{` field`}</a>{`.`}</p>
    <p>{`For example, you can select the SEEK advertiser ID that posted a given `}<a parentName="p" {...{
        "href": "/schema/named-type/PositionProfile"
      }}><inlineCode parentName="a">{`PositionProfile`}</inlineCode></a>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":\"lovato-ignore-no-deprecated-schema\",\"value\":\"query ($id: String!) {\\n  positionProfile(id: $id) {\\n    positionOrganizations {\\n      seekAnzAdvertiserId\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":135,\"column\":1,\"offset\":3113},\"end\":{\"line\":143,\"column\":4,\"offset\":3280},\"indent\":[1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"id\\\": \\\"seekAnzPublicTest:positionProfile:jobAd:287njieaj\\\"\\n}\",\"position\":{\"start\":{\"line\":145,\"column\":1,\"offset\":3282},\"end\":{\"line\":149,\"column\":4,\"offset\":3357},\"indent\":[1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"positionProfile\\\": {\\n    \\\"positionOrganizations\\\": [\\n      {\\n        \\\"seekAnzAdvertiserId\\\": 10001\\n      }\\n    ]\\n  }\\n}\",\"position\":{\"start\":{\"line\":151,\"column\":1,\"offset\":3359},\"end\":{\"line\":161,\"column\":4,\"offset\":3491},\"indent\":[1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":\"lovato-ignore-no-deprecated-schema\",\"value\":\"query": true,
        "($id:": true,
        "String!)": true,
        "{\\n": true,
        "": true,
        "positionProfile(id:": true,
        "$id)": true,
        "positionOrganizations": true,
        "seekAnzAdvertiserId\\n": true,
        "}\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":135,\"column\":1,\"offset\":3113},\"end\":{\"line\":143,\"column\":4,\"offset\":3280},\"indent\":[1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"id\\\":": true,
        "\\\"seekAnzPublicTest:positionProfile:jobAd:287njieaj\\\"\\n}\",\"position\":{\"start\":{\"line\":145,\"column\":1,\"offset\":3282},\"end\":{\"line\":149,\"column\":4,\"offset\":3357},\"indent\":[1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"positionProfile\\\":": true,
        "\\\"positionOrganizations\\\":": true,
        "[\\n": true,
        "\\\"seekAnzAdvertiserId\\\":": true,
        "10001\\n": true,
        "]\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":151,\"column\":1,\"offset\":3359},\"end\":{\"line\":161,\"column\":4,\"offset\":3491},\"indent\":[1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`query ($id: String!) {
  positionProfile(id: $id) {
    positionOrganizations {
      seekAnzAdvertiserId
    }
  }
}
`}</code></pre>
    <h2 {...{
      "id": "converting-to-a-seek-advertiser-id"
    }}>{`Converting to a SEEK advertiser ID`}</h2>
    <p>{`You can directly convert a hirer ID to a SEEK advertiser ID using the `}<a parentName="p" {...{
        "href": "/schema/query/hiringOrganization"
      }}><inlineCode parentName="a">{`hiringOrganization`}</inlineCode>{` query`}</a>{`.
This can be useful for exploratory testing or debugging:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":\"lovato-ignore-no-deprecated-schema\",\"value\":\"query ($id: String!) {\\n  hiringOrganization(id: $id) {\\n    seekAnzAdvertiserId\\n    name\\n  }\\n}\",\"position\":{\"start\":{\"line\":170,\"column\":1,\"offset\":3787},\"end\":{\"line\":177,\"column\":4,\"offset\":3930},\"indent\":[1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"id\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\"\\n}\",\"position\":{\"start\":{\"line\":179,\"column\":1,\"offset\":3932},\"end\":{\"line\":183,\"column\":4,\"offset\":4002},\"indent\":[1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"hiringOrganization\\\": {\\n    \\\"seekAnzAdvertiserId\\\": 10000,\\n    \\\"name\\\": \\\"Acme Corp\\\"\\n  }\\n}\",\"position\":{\"start\":{\"line\":185,\"column\":1,\"offset\":4004},\"end\":{\"line\":192,\"column\":4,\"offset\":4107},\"indent\":[1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":\"lovato-ignore-no-deprecated-schema\",\"value\":\"query": true,
        "($id:": true,
        "String!)": true,
        "{\\n": true,
        "": true,
        "hiringOrganization(id:": true,
        "$id)": true,
        "seekAnzAdvertiserId\\n": true,
        "name\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":170,\"column\":1,\"offset\":3787},\"end\":{\"line\":177,\"column\":4,\"offset\":3930},\"indent\":[1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"id\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\"\\n}\",\"position\":{\"start\":{\"line\":179,\"column\":1,\"offset\":3932},\"end\":{\"line\":183,\"column\":4,\"offset\":4002},\"indent\":[1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"hiringOrganization\\\":": true,
        "\\\"seekAnzAdvertiserId\\\":": true,
        "10000,\\n": true,
        "\\\"name\\\":": true,
        "\\\"Acme": true,
        "Corp\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":185,\"column\":1,\"offset\":4004},\"end\":{\"line\":192,\"column\":4,\"offset\":4107},\"indent\":[1,1,1,1,1,1,1]}}]": true
      }}>{`query ($id: String!) {
  hiringOrganization(id: $id) {
    seekAnzAdvertiserId
    name
  }
}
`}</code></pre>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;